import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "原创优先",
    description:
      "所有署名「抖阳原创」的内容，均由编辑部或签约作者独立完成。我们不接受未披露来源的转载，也不使用机器批量生成稿件。",
  },
  {
    icon: "ri-shield-check-line",
    title: "事实可核查",
    description:
      "涉及数据与政策的报道，都会标注来源与统计口径。一旦发现错误，我们会在原文顶部标注更正说明，而不是悄悄修改。",
  },
  {
    icon: "ri-heart-3-line",
    title: "尊重读者时间",
    description:
      "我们尽量把话说清楚，而不是把篇幅拉长。长文之所以长，是因为信息量需要，而不是因为字数考核。",
  },
];

const team = [
  { name: "陈默", role: "主编", bio: "二十年媒体从业者，负责选题终审与内容标准。", initials: "陈" },
  { name: "林知远", role: "文化主笔", bio: "关注城市文化空间、出版与手工艺现场。", initials: "林" },
  { name: "周映", role: "娱乐记者", bio: "跟踪影视工业化与新兴内容形态。", initials: "周" },
  { name: "苏晚", role: "生活方式编辑", bio: "写城市生活实验与消费观察。", initials: "苏" },
];

const timeline = [
  { year: "2023", title: "抖阳上线", description: "最初只有三个人和一周两篇的更新频率。" },
  { year: "2024", title: "视频栏目启动", description: "第一支原创纪录短片《老街二十四小时》上线。" },
  { year: "2025", title: "签约作者计划", description: "开放外部作者投稿，建立双周选题会机制。" },
  { year: "2026", title: "四个频道成型", description: "文化、娱乐、生活、观点形成稳定的内容结构。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 抖阳",
    description:
      "了解抖阳的定位、编辑原则、团队与发展历程。抖阳专注原创内容与多元资讯，坚持事实核查与长期主义。",
    keywords: "抖阳,关于我们,编辑原则,原创内容,内容平台",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100/70 px-4 pb-10 pt-5 md:px-6 md:pb-14 md:pt-6">
          <div className="mx-auto max-w-[1400px]">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
              <div className="md:col-span-7">
                <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">About Duyang</p>
                <h1 className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-5xl">
                  把内容做得慢一点，
                  <br className="hidden md:block" />
                  把人看得重一点
                </h1>
                <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  抖阳是一个集原创文章、视频与多领域资讯于一体的综合内容平台，覆盖文化、娱乐、生活与观点。我们服务的读者，是那些愿意为一段文字停留十分钟的人。
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <p className="text-xs leading-relaxed text-foreground-500">
                    「内容行业最稀缺的不是产能，而是判断。我们希望每一篇被发出来的稿子，都有人为它负责。」
                  </p>
                  <p className="mt-3 text-[11px] text-foreground-400">—— 抖阳编辑部</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们在做什么</h2>
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
                <p>
                  平台每天更新原创报道、视频与资讯速览。我们的报道对象很具体：一间撑了五年的书店、一位在高温窑前站了二十年的手艺人、一个三天拍完一部戏的剧组。宏大的判断往往不可靠，具体的人更值得记录。
                </p>
                <p>
                  除了原创内容，我们也会整理各领域的最新动态，但会明确标注信息来源。如果你在抖阳看到「综合整理」，意味着这条内容来自公开信息，我们没有进行独立采访。
                </p>
                <p>
                  抖阳目前没有会员体系、没有付费墙，也不提供下载。我们相信内容应该先被读到，再讨论如何活得久一点。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="group rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${category.icon} text-sm`} />
                    </span>
                    <p className="mt-2.5 text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                      {category.name}
                    </p>
                    <p className="mt-0.5 font-label text-[10px] uppercase tracking-[0.16em] text-foreground-400">
                      {category.en}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">内容政策要点</h2>
                <ul className="mt-4 space-y-3">
                  {[
                    "原创内容版权归抖阳与作者共同所有，转载需获得书面授权。",
                    "引用外部数据时标注具体来源与发布时间。",
                    "不接受以删除负面报道为条件的商业合作。",
                    "涉及个人隐私的报道，会与受访者确认最终表述。",
                    "发现错误请通过反馈表单告知，我们会在 48 小时内回应。",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
                      <i className="ri-check-line mt-0.5 text-primary-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100/60 px-4 py-10 md:px-6 md:py-14">
          <div className="mx-auto max-w-[1400px]">
            <SectionHeading
              eyebrow="Principles"
              title="三条编辑原则"
              description="它们不是口号，而是我们每天都要面对的取舍。"
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {principles.map((item, index) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <span className="font-editorial text-xs text-background-400">0{index + 1}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <SectionHeading
            eyebrow="Team"
            title="编辑部与签约作者"
            description="一支不大的团队，和一群长期合作的外部作者。"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-bold text-primary-700">
                  {member.initials}
                </span>
                <h3 className="mt-3 text-sm font-semibold text-foreground-950">{member.name}</h3>
                <p className="mt-0.5 text-xs text-primary-600">{member.role}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500">{member.bio}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-6 md:py-14">
          <div className="mx-auto max-w-[1400px]">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">Milestones</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">发展愿景</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  未来三年，我们希望把四个频道做成可持续的内容体系：有稳定的作者，有可被检索的档案，也有足够克制的商业化方式。不追求覆盖所有热点，只保证每一条发出去的内容都站得住。
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link
                    to="/contact"
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 hover:bg-primary-600"
                  >
                    联系编辑部
                    <i className="ri-mail-line" />
                  </Link>
                  <Link
                    to="/search?q=城市"
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  >
                    阅读代表作品
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
              <div className="md:col-span-7">
                <ol className="relative space-y-5 border-l border-background-300 pl-5">
                  {timeline.map((item) => (
                    <li key={item.year} className="relative">
                      <span className="absolute -left-[27px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background-50 bg-primary-500" />
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-editorial text-sm font-bold text-primary-600">{item.year}</span>
                        <span className="text-sm font-medium text-foreground-900">{item.title}</span>
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-500">{item.description}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="rounded-lg bg-primary-950 p-6 md:p-10">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-300">Cooperation</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-background-50 md:text-2xl">
                  版权、转载与内容合作
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
                  转载与授权请联系 editor@duyang.example，邮件中请注明媒体名称、转载内容与发布渠道。品牌合作与内容共创请在反馈表单中说明需求，我们会在三个工作日内回复。
                </p>
              </div>
              <Link
                to="/contact"
                className="flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-100"
              >
                前往联系页面
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}